import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { cn } from '@/lib/utils'

interface PaginationProps {
    basePath: string
    page: number
    totalPages: number
}

const buildHref = (basePath: string, n: number): string =>
    n <= 1 ? basePath : `${basePath}?page=${n}`

export const Pagination: FC<PaginationProps> = ({ basePath, page, totalPages }): ReactNode => {
    if (totalPages <= 1) return null

    const numbers: (number | 'gap')[] = []

    for (let i = 1; i <= totalPages; i++) {
        if (i === 1 || i === totalPages || Math.abs(i - page) <= 1) {
            numbers.push(i)
        } else if (numbers[numbers.length - 1] !== 'gap') {
            numbers.push('gap')
        }
    }

    const edgeClass =
        'inline-flex h-9 min-w-9 items-center justify-center rounded-md border border-border bg-card px-2 text-sm font-medium transition-colors hover:bg-secondary'

    return (
        <nav className='flex items-center justify-center gap-1.5' aria-label='Pagination'>
            {page > 1 ? (
                <Link href={buildHref(basePath, page - 1)} className={edgeClass} aria-label='Previous page'>
                    <ChevronLeft className='size-4' />
                </Link>
            ) : (
                <span className={cn(edgeClass, 'cursor-not-allowed opacity-40')} aria-hidden='true'>
                    <ChevronLeft className='size-4' />
                </span>
            )}

            {numbers.map((n, index) =>
                n === 'gap' ? (
                    <span key={`gap-${index}`} className='px-1 text-sm text-muted-foreground'>
                        …
                    </span>
                ) : (
                    <Link
                        key={n}
                        href={buildHref(basePath, n)}
                        aria-current={n === page ? 'page' : undefined}
                        className={cn(
                            'inline-flex h-9 min-w-9 items-center justify-center rounded-md border px-2 text-sm font-medium transition-colors',
                            n === page
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border bg-card hover:bg-secondary'
                        )}
                    >
                        {n}
                    </Link>
                )
            )}

            {page < totalPages ? (
                <Link href={buildHref(basePath, page + 1)} className={edgeClass} aria-label='Next page'>
                    <ChevronRight className='size-4' />
                </Link>
            ) : (
                <span className={cn(edgeClass, 'cursor-not-allowed opacity-40')} aria-hidden='true'>
                    <ChevronRight className='size-4' />
                </span>
            )}
        </nav>
    )
}
